/**
 * Clerk webhook-subscription issues — what the daily observation check found on
 * its two independent axes.
 *
 * Both are PLATFORM rows: a subscription is a property of the one Clerk webhook
 * endpoint, never of a tenant. They hold separate rows on purpose, so a run where
 * both are true cannot let either one hide the other.
 */
import type { IssueDefinition } from "../issue-definition";
import { ISSUE_CATEGORY, ISSUE_FAULT } from "../issue-enums";

export const CLERK_EVENT_OBSERVATION_ISSUES = {
  CLERK_EVENT_TYPE_NEVER_DELIVERED: {
    category: ISSUE_CATEGORY.WEBHOOK,
    message:
      "Clerk event types we declare a subscription to have never been delivered — their handlers have never run",
    severity: "high",
    fault: ISSUE_FAULT.NEXUS,
    // Pages: a declared type that never arrives is a handler mirroring nothing into
    // Postgres. `organizationMembership.created` sat handled-but-never-delivered from
    // 2024-12-30 to 2026-08-08 because nothing compared the two. A run whose control
    // proves records were LOST reports this at `critical`, which escalates and pages again.
    page: true,
    remedy:
      "Each type on rawPayload `missing` is named in `CLERK_SUBSCRIBED_EVENTS` (apps/backend/src/users/clerk-webhook-subscriptions.ts) and appears in no delivery ever recorded. Three causes fit and the arrival record cannot separate them: the event is not ticked on the Clerk webhook endpoint; it is ticked and Clerk is not sending it; or the business event has simply never happened.\n" +
      "1. Read rawPayload `controls`, one per missing type. `contradicted` means Clerk holds more of that object than we mirror — real lost data, and ticking the box does NOT backfill it. `consistent` means nothing has been lost and there may be nothing to do. `unestablished` means the check could not decide (its `reason` says why) — never that the type is fine.\n" +
      "2. When you tick a box, match the name character for character: Clerk's instance-level `invitation.*` is a DIFFERENT group from `organizationInvitation.*`, this codebase handles none of it, and subscribing it raises CLERK_EVENT_TYPE_UNDECLARED instead of fixing anything.\n" +
      "3. If the declaration is what is wrong, delete the line from `CLERK_SUBSCRIBED_EVENTS`.\n" +
      "This issue resolves itself on the first daily check that finds every declared type delivered at least once."
  },
  CLERK_EVENT_TYPE_UNDECLARED: {
    category: ISSUE_CATEGORY.WEBHOOK,
    message:
      "Clerk is delivering event types this codebase declares nowhere — we answer 200 and do nothing with them",
    severity: "medium",
    fault: ISSUE_FAULT.NEXUS,
    // Not paged: the fix is a code decision, nothing is lost by it waiting for the
    // next working day, and the delivery keeps being recorded either way.
    remedy:
      "Each type on rawPayload `types` arrived recently and appears in neither `CLERK_SUBSCRIBED_EVENTS` nor `CLERK_UNHANDLED_EVENTS`. `dispatchClerkEvent` in apps/backend/src/users/users.controller.ts falls through to `return undefined` and the endpoint answers 200, so Clerk records a successful delivery for work nobody did.\n" +
      "Two acts end this today, and they are different decisions. HANDLE IT — add the dispatch arm and the line in `CLERK_SUBSCRIBED_EVENTS`. Or ACKNOWLEDGE IT — add the type to `CLERK_UNHANDLED_EVENTS`.\n" +
      "Unticking the event on the Clerk dashboard does NOT end it today: nothing deletes an arrival row, so the type stays reported until its last delivery falls outside the recency window on rawPayload.\n" +
      "This issue resolves itself on the first daily check that finds every recent type accounted for."
  }
} as const satisfies Record<string, IssueDefinition>;
